"use client";

import { ReactNode, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import classnames from "classnames";

export interface TabItem {
  id: string;
  label: string;
  content?: ReactNode;
  href?: string;
  badgeCount?: number;
}

interface TabProps {
  tabs: TabItem[];
  defaultTab?: string;
  onChange?: (tabId: string) => void;
  className?: string;
}

export default function Tab({
  tabs,
  defaultTab,
  onChange,
  className,
}: TabProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id || "");

  const handleTabChange = (tabId: string, href?: string) => {
    if (href) {
      // If clicking the already-active tab, trigger onChange before navigation
      if (pathname === href) {
        onChange?.(tabId);
      }
      router.push(href);
    } else {
      setActiveTab(tabId);
      onChange?.(tabId);
    }
  };

  return (
    <div data-testid="tab" className={classnames("w-full", className)}>
      {/* Tab Headers */}
      <div
        data-testid="tab-header-list"
        className="flex items-center justify-center border-b border-gray-200"
      >
        {tabs.map((tab) => {
          const isActive = tab.href
            ? pathname === tab.href
            : activeTab === tab.id;
          return (
            <div
              key={tab.id}
              data-testid={`tab-header-${tab.id}`}
              data-active={isActive}
              onClick={() => handleTabChange(tab.id, tab.href)}
              className={classnames(
                "h-11 text-center text-ng-font-1 transition-colors w-full",
                "focus:outline-none",
                "flex items-center justify-center gap-2",
                {
                  "border-b-3 border-ng-main": isActive,
                  "text-gray-600 hover:text-gray-800": !isActive,
                },
              )}
            >
              <div
                data-testid={`tab-header-${tab.id}-label`}
                className={classnames("text-[16px] font-bold leading-[140%]")}
              >
                {tab.label}
              </div>
              {(tab.badgeCount ?? 0) > 0 && (
                <span
                  data-testid={`tab-header-${tab.id}-badge`}
                  className={classnames(
                    "inline-block w-5 h-5 bg-[#ACACAC] text-sm font-semibold text-white rounded-full",
                  )}
                >
                  {tab.badgeCount}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Tab Content */}
      {tabs.some((tab) => tab.content) && (
        <div data-testid="tab-panel-list" className="mt-4">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              data-testid={`tab-panel-${tab.id}`}
              data-active={activeTab === tab.id}
              className={classnames({
                hidden: activeTab !== tab.id,
              })}
            >
              {tab.content}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
